import React, { lazy, Suspense, useState } from "react";
import { Sparkles, Landmark, Crown, FileSpreadsheet, ChevronDown } from "lucide-react";
import { AllMethodologiesResult } from "../types";

const TargetReportCard = lazy(() => import("./TargetReportCard"));
const ANYskinTab = lazy(() => import("./ANYskinTab"));
const CreditTab = lazy(() => import("./CreditTab"));
const SolvencyTab = lazy(() => import("./SolvencyTab"));
const ScoreTab = lazy(() => import("./ScoreTab"));
const KrylovTab = lazy(() => import("./KrylovTab"));
const CompareTab = lazy(() => import("./CompareTab"));
const FinancesTab = lazy(() => import("./FinancesTab"));
const RelatedTab = lazy(() => import("./RelatedTab"));
const MethodologyView = lazy(() => import("./MethodologyView"));
const BankMethodologiesTab = lazy(() => import("./BankMethodologiesTab"));

// Локальный fallback вместо общего на весь экран (App.tsx) — иначе первое
// открытие ЛЮБОГО блока (вкладки, «Данные клиента») на миг гасило всю
// страницу целиком, а не только этот блок (#экран «мигает» при раскрытии).
const SectionFallback = () => (
  <div className="py-10 text-center text-xs text-slate-400 animate-pulse">Загрузка…</div>
);

export type ResultTab = "anyskin" | "credit" | "solvency" | "score" | "krylov" | "compare"
  | "finances" | "related" | "custom" | "banks";

interface Props {
  result: AllMethodologiesResult;
  activeTab: ResultTab;
  setActiveTab: (tab: ResultTab) => void;
  activeCustomId: number | null;
  setActiveCustomId: (id: number) => void;
  isPremium: boolean;
  setPremiumModal: (message: string | null) => void;
  reportBusy: boolean;
  onFullReport: () => void;
  onAskAssistant: (question: string) => void;
  onOpenBankPicker: () => void;
  isLoading: boolean;
  onRecalcTarget: () => void;
  onRecalcKrylovTarget: () => void;
  onExportCsv: () => void;
}

// Вкладки методик (1-5), банковские методики, данные клиента (отчётность/связи)
// и содержимое активной вкладки — вместе с «Рекомендованным балансом» внизу.
// Чистая презентация: вся бизнес-логика (расчёт, экспорт, PDF) — в App.tsx.
export default function ResultTabs({
  result, activeTab, setActiveTab, activeCustomId, setActiveCustomId,
  isPremium, setPremiumModal, reportBusy, onFullReport, onAskAssistant,
  onOpenBankPicker, isLoading, onRecalcTarget, onRecalcKrylovTarget, onExportCsv,
}: Props) {
  // Данные клиента (отчётность/связи) — не методики, поэтому не вкладки в этой
  // навигации: свёрнутые строки над ней, раскрываются по клику.
  const [openData, setOpenData] = useState<"finances" | "related" | null>(null);

  return (
    <>
      {/* Данные клиента — свёрнутая табличка над методиками */}
      <div className="mb-4 bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100">
        <span className="t-micro text-slate-400 uppercase tracking-widest block px-4 pt-3">Данные клиента</span>
        <button onClick={() => setOpenData((v) => v === "finances" ? null : "finances")}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 cursor-pointer">
          <span className="text-xs font-semibold text-slate-700">Отчётность и Динамика</span>
          <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openData === "finances" ? "rotate-180" : ""}`} />
        </button>
        {openData === "finances" && (
          <div className="px-4 pb-4"><Suspense fallback={<SectionFallback />}><FinancesTab data={result.finances} /></Suspense></div>
        )}
        <button onClick={() => setOpenData((v) => v === "related" ? null : "related")}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 cursor-pointer">
          <span className={`text-xs font-semibold ${result.bankruptcy?.isBankrupt ? "text-rose-600" : "text-slate-700"}`}>
            Связи и банкротство{result.bankruptcy?.isBankrupt ? " ⚠" : ""}
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openData === "related" ? "rotate-180" : ""}`} />
        </button>
        {openData === "related" && (
          <div className="px-4 pb-4">
            <Suspense fallback={<SectionFallback />}>
              <RelatedTab bankruptcy={result.bankruptcy} related={result.related} subject={result.company} />
            </Suspense>
          </div>
        )}
      </div>

      {/* Navigation Dashboard Tabs Header */}
      <div className="border-b border-slate-200">
        <nav className="flex flex-wrap gap-x-1.5 gap-y-1 -mb-px">
          <button
            onClick={() => setActiveTab("compare")}
            className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer ${
              activeTab === "compare"
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span className="md:hidden">Сводный</span>
            <span className="hidden md:inline">Сводный отчет</span>
          </button>
          <button
            onClick={() => setActiveTab("anyskin")}
            className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer ${
              activeTab === "anyskin"
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span className="md:hidden">ANYskin</span>
            <span className="hidden md:inline">1. ANYskin (Рейтинг)</span>
          </button>
          <button
            onClick={() => setActiveTab("credit")}
            className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer ${
              activeTab === "credit"
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span className="md:hidden">Кредит</span>
            <span className="hidden md:inline">2. Кредитоспособность (К1-К7)</span>
          </button>
          <button
            onClick={() => setActiveTab("solvency")}
            className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer ${
              activeTab === "solvency"
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span className="md:hidden">Платёжеспос.</span>
            <span className="hidden md:inline">3. Платежеспособность (Л1-Л9)</span>
          </button>
          <button
            onClick={() => setActiveTab("score")}
            className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer ${
              activeTab === "score"
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <span className="md:hidden">Потенциал</span>
            <span className="hidden md:inline">4. Потенциал кредита</span>
          </button>
          {result.krylov?.calculated && (
            <button
              onClick={() => setActiveTab("krylov")}
              className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer ${
                activeTab === "krylov"
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <span className="md:hidden">Угл. анализ</span>
              <span className="hidden md:inline">5. Углублённый анализ</span>
            </button>
          )}

          {/* Динамические вкладки пользовательских методик (видны их владельцу). */}
          {result.custom && result.custom.length > 0 && (
            <span className="self-stretch w-px bg-slate-200 mx-1 hidden md:block" aria-hidden />
          )}
          {(result.custom || []).map((c) => (
            <button
              key={c.id}
              onClick={() => { setActiveTab("custom"); setActiveCustomId(c.id); }}
              className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer inline-flex items-center gap-1 ${
                activeTab === "custom" && activeCustomId === c.id
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
              title={c.name}
            >
              <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />
              <span className="max-w-[120px] truncate">{c.name}</span>
            </button>
          ))}

          {/* Банковские методики — одна вкладка со сводной таблицей (BankMethodologiesTab),
              а не кнопка на каждый банк. Доступна только премиуму — без него клик
              не переключает таб, открывает апселл. */}
          <span className="self-stretch w-px bg-slate-200 mx-1 hidden md:block" aria-hidden />
          <button
            onClick={() => isPremium ? setActiveTab("banks")
              : setPremiumModal("Выбор банковских методик для анализа — премиум-функция.")}
            className={`whitespace-nowrap pb-3 px-2.5 md:px-4 text-xs font-semibold border-b-2 font-display transition-all cursor-pointer inline-flex items-center gap-1 ${
              activeTab === "banks"
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
            title={isPremium ? "Банковские методики" : "Банковские методики — премиум"}
          >
            <Landmark className="w-3.5 h-3.5 shrink-0" />
            <span className="md:hidden">Банки</span>
            <span className="hidden md:inline">Банковские методики</span>
            {!isPremium && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
          </button>
        </nav>
      </div>

      {/* PDF-отчёт по текущей методике (фирменный, печать → PDF). */}
      {activeTab !== "custom" && activeTab !== "banks" && (
        <div className="flex justify-end mb-3">
          <button
            onClick={onFullReport} disabled={reportBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-bold rounded-lg cursor-pointer"
            title="Полный аналитический отчёт по клиенту (все методики, отчётность/динамика, опыт, связи, арбитражи, целевой баланс) → печать/PDF. Связи догружаются автоматически."
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> {reportBusy ? "Готовлю отчёт…" : "Полный отчёт (PDF)"}
          </button>
        </div>
      )}

      {/* Conditional Rendering Active Tab Frame */}
      <div className="transition-all duration-300">
        <Suspense fallback={<SectionFallback />}>
          {activeTab === "anyskin" && <ANYskinTab data={result.anyskin} onAskAssistant={onAskAssistant} />}
          {activeTab === "credit" && <CreditTab data={result.credit} onAskAssistant={onAskAssistant} />}
          {activeTab === "solvency" && <SolvencyTab data={result.solvency} onAskAssistant={onAskAssistant} />}
          {activeTab === "score" && <ScoreTab data={result.score} onAskAssistant={onAskAssistant} />}
          {activeTab === "krylov" && <KrylovTab data={result.krylov} onAskAssistant={onAskAssistant}
            onRecalcByTarget={onRecalcKrylovTarget} recalcBusy={isLoading} />}
          {activeTab === "compare" && <CompareTab data={result} onExportCsv={onExportCsv} />}
          {activeTab === "custom" && activeCustomId != null && (() => {
            const c = result.custom?.find((x) => x.id === activeCustomId);
            return c ? <MethodologyView data={c} onAskAssistant={onAskAssistant} /> : (
              <p className="text-xs text-slate-400">Выберите методику на вкладке.</p>
            );
          })()}
          {activeTab === "banks" && (
            <BankMethodologiesTab
              bankResults={result.bankResults || []}
              onOpenPicker={onOpenBankPicker}
              onAskAssistant={onAskAssistant}
            />
          )}
        </Suspense>
      </div>

      {/* Рекомендованный баланс — под любой методикой (не на custom),
          с возможностью пересчитать всё по этому варианту (проекция). */}
      {result.targetReport && activeTab !== "custom" && activeTab !== "banks" && activeTab !== "krylov" && (
        <div className="mt-6">
          <Suspense fallback={<SectionFallback />}>
            <TargetReportCard report={result.targetReport} onRecalc={onRecalcTarget} recalcBusy={isLoading} />
          </Suspense>
        </div>
      )}
    </>
  );
}
